import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import App from 'antd/lib/app';
import { QueryKey } from '../../query-client';
import { API } from '../api';
import { DEFAULT_PAGINATION } from '../commonConst';
import {
    IEmailUtilityAutomation,
    IEmailUtilityAutomationHistory,
    IEmailUtilityAutomationHistoryContent,
    IFetchEmailUtilityAutomationHistoryQuery,
    IUpdateEmailUtilityAutomation,
} from './interface';

export const fetchEmailUtilityAutomations = async (signal?: AbortSignal): Promise<IEmailUtilityAutomation[]> => {
    const path = `/email-utility-automations`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(response.statusText);
};

export const fetchEmailUtilityAutomationHistory = async (
    query: IFetchEmailUtilityAutomationHistoryQuery,
    signal?: AbortSignal
): Promise<IEmailUtilityAutomationHistory[]> => {
    const searchParams = new URLSearchParams();
    if (query.limit !== undefined) searchParams.set('limit', String(query.limit));
    if (query.offset) searchParams.set('offset', String(query.offset));

    const queryString = searchParams.toString();
    const path = `/email-utility-automations/${query.automationKey}/history${queryString ? `?${queryString}` : ''}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot fetch email utility automation history!`);
};

export const fetchEmailUtilityAutomationHistoryContent = async (
    messageId: string,
    signal?: AbortSignal
): Promise<IEmailUtilityAutomationHistoryContent> => {
    const path = `/email-utility-automations/history/${messageId}/content`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot fetch email utility automation history content!`);
};

const updateEmailUtilityAutomation = async ({ automation_key, ...updateEmailUtilityAutomation }: IUpdateEmailUtilityAutomation) => {
    const path = `/email-utility-automations/${automation_key}`;

    const response = await API.fetch(path, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateEmailUtilityAutomation),
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return;
    }

    throw new Error(respBody?.error?.message || `Cannot save email utility automation!`);
};

/****************************** */
export const useEmailUtilityAutomationListQuery = (query: {}, options = {}) => {
    return useQuery<IEmailUtilityAutomation[], Error>(
        [QueryKey.EMAIL_UTILITY_AUTOMATIONS, query],
        ({ signal }) => fetchEmailUtilityAutomations(signal),
        {
            keepPreviousData: true,
            ...options,
        }
    );
};

export const useEmailUtilityAutomationHistoryListQuery = (query: IFetchEmailUtilityAutomationHistoryQuery, options = {}) => {
    return useQuery<IEmailUtilityAutomationHistory[], Error>(
        [QueryKey.EMAIL_UTILITY_AUTOMATION_HISTORY, query],
        ({ signal }) => fetchEmailUtilityAutomationHistory(query, signal),
        {
            keepPreviousData: true,
            ...options,
        }
    );
};

/**
 * Pages the parser history newest-first. The API returns a bare array, so a page shorter than
 * the requested size is what tells us we have reached the end of the 12-month window.
 */
export const useEmailUtilityAutomationHistoryInfiniteListQuery = (automationKey: string, options = {}) => {
    const pageSize = DEFAULT_PAGINATION.pageSize;

    return useInfiniteQuery<IEmailUtilityAutomationHistory[], Error>({
        queryKey: [QueryKey.EMAIL_UTILITY_AUTOMATION_HISTORY, { automationKey, infinite: true }],
        queryFn: ({ pageParam = 0, signal }) => fetchEmailUtilityAutomationHistory({ automationKey, limit: pageSize, offset: pageParam }, signal),
        getNextPageParam: (lastPage, pages) => (lastPage.length < pageSize ? undefined : pages.length * pageSize),
        keepPreviousData: true,
        ...options,
    });
};

export const useEmailUtilityAutomationHistoryContentQuery = (messageId: string | null, options = {}) => {
    return useQuery<IEmailUtilityAutomationHistoryContent, Error>(
        [QueryKey.EMAIL_UTILITY_AUTOMATION_HISTORY_CONTENT, messageId],
        ({ signal }) => fetchEmailUtilityAutomationHistoryContent(messageId as string, signal),
        {
            enabled: !!messageId,
            staleTime: Infinity, // a stored message's content never changes once parsed
            retry: false, // a failed fetch (e.g. deleted/archived message) won't succeed on retry - surface the error immediately instead of spinning through ~14s of default retries
            ...options,
        }
    );
};

export const useEmailUtilityAutomationUpdateMutation = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<void, Error, IUpdateEmailUtilityAutomation>(updateEmailUtilityAutomation, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([QueryKey.EMAIL_UTILITY_AUTOMATIONS]);
            notification.success({ key: 'utility-automation-success', message: 'Utility Automation saved!' });
        },
    });
};
